import { useEffect, useState } from "react";
import { useRoute, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Shield,
  FileText,
  User,
  Clock,
  Hash,
  Building,
  Globe,
  Download,
  ArrowLeft,
  Lock,
  Fingerprint,
  Calendar,
  MapPin,
} from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { motion } from "framer-motion";

interface VerificationResult {
  isValid: boolean;
  status: "valid" | "invalid" | "modified" | "certificate_revoked" | "expired" | "not_found";
  document: {
    id: string;
    name: string;
    createdAt: string;
    completedAt: string | null;
    totalSigners: number;
    completedSigners: number;
  } | null;
  signatures: Array<{
    id: string;
    signerName: string;
    signerEmail: string;
    signedAt: string;
    signatureMethod: string;
    identityVerificationMethod: string | null;
    ipAddress: string | null;
    validity: string;
    documentHashAtSigning: string;
    timestampToken: string | null;
    timestampAuthority: string | null;
  }>;
  documentHash: {
    algorithm: string;
    value: string;
    createdAt: string;
  } | null;
  legalFramework: string;
  jurisdiction: string;
  integrityCheck: {
    passed: boolean;
    message: string;
  };
  certificateOfCompletion: {
    id: string;
    certificateHash: string;
    completedAt: string | null;
  } | null;
  accessInfo: {
    accessCount: number;
    lastAccessedAt: string | null;
    currentIp: string;
  };
}

const translations = {
  en: {
    title: "Signature Verification Portal",
    subtitle: "Verify the authenticity and integrity of digitally signed documents",
    verifying: "Verifying Document...",
    documentInfo: "Document Information",
    signaturesTitle: "Digital Signatures",
    integrityTitle: "Document Integrity",
    legalCompliance: "Legal Compliance",
    accessLog: "Verification Access Log",
    documentName: "Document Name",
    createdAt: "Created",
    completedAt: "Completed",
    signers: "Signers",
    signedBy: "Signed by",
    signedAt: "Signed at",
    signatureMethod: "Signature Method",
    identityVerification: "Identity Verification",
    ipAddress: "IP Address",
    documentHash: "Document Hash",
    hashAlgorithm: "Hash Algorithm",
    timestampAuthority: "Timestamp Authority",
    timestampApplied: "Timestamp Applied",
    framework: "Legal Framework",
    jurisdiction: "Jurisdiction",
    integrityPassed: "Document integrity verified",
    integrityFailed: "Document integrity check failed",
    accessCount: "Times Verified",
    lastAccessed: "Last Verified",
    yourIp: "Your IP Address",
    valid: "Valid",
    invalid: "Invalid",
    modified: "Document Modified",
    expired: "Expired",
    revoked: "Certificate Revoked",
    notFound: "Document Not Found",
    downloadCertificate: "Download Certificate of Completion",
    backToHome: "Back to Home",
    noToken: "No verification code in the link.",
    draw: "Hand-drawn Signature",
    govbr: "GOV.BR Digital Certificate",
    typed: "Typed Signature",
    verificationSuccess: "This document has been verified and is legally valid",
    verificationFailed: "Verification failed. This document may have been tampered with.",
    documentNotFound: "The document associated with this verification link was not found.",
    signatureOf: "of",
    signaturesComplete: "signatures complete",
    brazil: "Brazil",
    eu: "European Union",
    us: "United States",
    international: "International",
    trustedBy: "FillSign - Trusted Digital Signatures",
  },
  pt: {
    title: "Portal de Verificação de Assinaturas",
    subtitle: "Verifique a autenticidade e integridade de documentos assinados digitalmente",
    verifying: "Verificando Documento...",
    documentInfo: "Informações do Documento",
    signaturesTitle: "Assinaturas Digitais",
    integrityTitle: "Integridade do Documento",
    legalCompliance: "Conformidade Legal",
    accessLog: "Registro de Acesso de Verificação",
    documentName: "Nome do Documento",
    createdAt: "Criado em",
    completedAt: "Concluído em",
    signers: "Assinantes",
    signedBy: "Assinado por",
    signedAt: "Assinado em",
    signatureMethod: "Método de Assinatura",
    identityVerification: "Verificação de Identidade",
    ipAddress: "Endereço IP",
    documentHash: "Hash do Documento",
    hashAlgorithm: "Algoritmo de Hash",
    timestampAuthority: "Autoridade de Carimbo de Tempo",
    timestampApplied: "Carimbo de Tempo Aplicado",
    framework: "Marco Legal",
    jurisdiction: "Jurisdição",
    integrityPassed: "Integridade do documento verificada",
    integrityFailed: "Verificação de integridade do documento falhou",
    accessCount: "Vezes Verificado",
    lastAccessed: "Última Verificação",
    yourIp: "Seu Endereço IP",
    valid: "Válido",
    invalid: "Inválido",
    modified: "Documento Modificado",
    expired: "Expirado",
    revoked: "Certificado Revogado",
    notFound: "Documento Não Encontrado",
    downloadCertificate: "Baixar Certificado de Conclusão",
    backToHome: "Voltar ao Início",
    noToken: "O link não tem o código de verificação.",
    draw: "Assinatura Manuscrita",
    govbr: "Certificado Digital GOV.BR",
    typed: "Assinatura Digitada",
    verificationSuccess: "Este documento foi verificado e é legalmente válido",
    verificationFailed: "Verificação falhou. Este documento pode ter sido adulterado.",
    documentNotFound: "O documento associado a este link de verificação não foi encontrado.",
    signatureOf: "de",
    signaturesComplete: "assinaturas completas",
    brazil: "Brasil",
    eu: "União Europeia",
    us: "Estados Unidos",
    international: "Internacional",
    trustedBy: "FillSign - Assinaturas Digitais Confiáveis",
  },
};

function StatusBadge({ status, t }: { status: string; t: typeof translations.en }) {
  const variants: Record<string, { variant: "default" | "destructive" | "outline" | "secondary"; icon: any }> = {
    valid: { variant: "default", icon: CheckCircle },
    invalid: { variant: "destructive", icon: XCircle },
    modified: { variant: "destructive", icon: AlertTriangle },
    expired: { variant: "secondary", icon: Clock },
    certificate_revoked: { variant: "destructive", icon: XCircle },
    not_found: { variant: "outline", icon: AlertTriangle },
  };

  const config = variants[status] || variants.invalid;
  const Icon = config.icon;
  const label = t[status as keyof typeof t] || status;

  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

function SignatureMethodBadge({ method, t }: { method: string; t: typeof translations.en }) {
  const methodLabels: Record<string, keyof typeof translations.en> = {
    draw: "draw",
    govbr: "govbr",
    typed: "typed",
  };

  const icons: Record<string, any> = {
    draw: Fingerprint,
    govbr: Shield,
    typed: FileText,
  };

  const Icon = icons[method] || FileText;
  const label = t[methodLabels[method] as keyof typeof t] || method;

  return (
    <Badge variant="outline" className="gap-1">
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
}

export default function VerifyPage() {
  const [, params] = useRoute("/verify/:token");
  const token = params?.token;
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const { data, isLoading, error } = useQuery<VerificationResult>({
    queryKey: ["/api/verify", token],
    enabled: !!token,
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <AlertTriangle className="h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">{t.noToken}</p>
              <Link href="/">
                <Button data-testid="button-back-home">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t.backToHome}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-semibold">FillSign</span>
            <Badge variant="secondary" className="text-xs">{t.title}</Badge>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {isLoading ? (
          <div className="space-y-6">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Shield className="h-16 w-16 text-primary mx-auto" />
              </motion.div>
              <p className="mt-4 text-lg text-muted-foreground">{t.verifying}</p>
            </div>
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          </div>
        ) : error || !data ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>{t.notFound}</AlertTitle>
              <AlertDescription>{t.documentNotFound}</AlertDescription>
            </Alert>
            <div className="mt-6 text-center">
              <Link href="/">
                <Button data-testid="button-back-home">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t.backToHome}
                </Button>
              </Link>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Alert variant={data.isValid ? "default" : "destructive"} className="border-2">
              {data.isValid ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5" />
              )}
              <AlertTitle className="text-lg">
                <StatusBadge status={data.status} t={t} />
              </AlertTitle>
              <AlertDescription className="mt-2">
                {data.isValid ? t.verificationSuccess : t.verificationFailed}
              </AlertDescription>
            </Alert>

            {data.document && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      {t.documentInfo}
                    </CardTitle>
                  </div>
                  <StatusBadge status={data.status} t={t} />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t.documentName}</p>
                      <p className="font-medium">{data.document.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t.signers}</p>
                      <p className="font-medium">
                        {data.document.completedSigners} {t.signatureOf} {data.document.totalSigners} {t.signaturesComplete}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t.createdAt}</p>
                      <p className="font-medium">
                        {new Date(data.document.createdAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}
                      </p>
                    </div>
                    {data.document.completedAt && (
                      <div>
                        <p className="text-sm text-muted-foreground">{t.completedAt}</p>
                        <p className="font-medium">
                          {new Date(data.document.completedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {data.documentHash && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Hash className="h-5 w-5" />
                    {t.integrityTitle}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert variant={data.integrityCheck.passed ? "default" : "destructive"}>
                    {data.integrityCheck.passed ? (
                      <Lock className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-4 w-4" />
                    )}
                    <AlertDescription>
                      {data.integrityCheck.passed ? t.integrityPassed : t.integrityFailed}
                    </AlertDescription>
                  </Alert>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t.hashAlgorithm}</p>
                      <p className="font-mono text-sm">{data.documentHash.algorithm}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t.documentHash}</p>
                      <p className="font-mono text-xs break-all bg-muted p-2 rounded-md">
                        {data.documentHash.value}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {data.signatures.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Fingerprint className="h-5 w-5" />
                    {t.signaturesTitle} ({data.signatures.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {data.signatures.map((sig, index) => (
                    <div key={sig.id}>
                      {index > 0 && <Separator className="my-4" />}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{sig.signerName}</span>
                            <span className="text-sm text-muted-foreground">({sig.signerEmail})</span>
                          </div>
                          <StatusBadge status={sig.validity} t={t} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">{t.signedAt}:</span>
                            <span>{new Date(sig.signedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Fingerprint className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">{t.signatureMethod}:</span>
                            <SignatureMethodBadge method={sig.signatureMethod} t={t} />
                          </div>
                          {sig.ipAddress && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{t.ipAddress}:</span>
                              <span className="font-mono text-xs">{sig.ipAddress}</span>
                            </div>
                          )}
                          {sig.timestampAuthority && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">{t.timestampAuthority}:</span>
                              <span>{sig.timestampAuthority}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-xs">
                          <p className="text-muted-foreground">{t.documentHash}:</p>
                          <p className="font-mono break-all bg-muted p-1 rounded">{sig.documentHashAtSigning}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  {t.legalCompliance}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t.jurisdiction}</p>
                    <p className="font-medium">{t[data.jurisdiction as keyof typeof t] || data.jurisdiction}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t.framework}</p>
                    <p className="font-medium">{data.legalFramework}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  {t.accessLog}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t.accessCount}</p>
                    <p className="font-medium">{data.accessInfo.accessCount}</p>
                  </div>
                  {data.accessInfo.lastAccessedAt && (
                    <div>
                      <p className="text-muted-foreground">{t.lastAccessed}</p>
                      <p className="font-medium">
                        {new Date(data.accessInfo.lastAccessedAt).toLocaleString(language === "pt" ? "pt-BR" : "en-US")}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">{t.yourIp}</p>
                    <p className="font-mono text-xs">{data.accessInfo.currentIp}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center justify-center gap-4">
              {data.certificateOfCompletion && (
                <Button data-testid="button-download-certificate">
                  <Download className="h-4 w-4 mr-2" />
                  {t.downloadCertificate}
                </Button>
              )}
              <Link href="/">
                <Button variant="outline" data-testid="button-back-home">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t.backToHome}
                </Button>
              </Link>
            </div>

            <div className="text-center text-sm text-muted-foreground pt-8 border-t">
              <p>{t.trustedBy}</p>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
